import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { FaCommentDots, FaPaperPlane, FaTimes } from 'react-icons/fa';
import { API_BASE } from '../config/api';
import './Chatbot.css';

const CHATBOT_LABELS = {
  fr: { title: 'Assistant RIVE', welcome: 'Bonjour ! Comment puis-je vous aider ?', placeholder: 'Posez votre question...', send: 'Envoyer', typing: 'En train d\'écrire...' },
  en: { title: 'RIVE Assistant', welcome: 'Hello! How can I help you?', placeholder: 'Ask a question...', send: 'Send', typing: 'Typing...' },
  es: { title: 'Asistente RIVE', welcome: '¡Hola! ¿Cómo puedo ayudarle?', placeholder: 'Haga su pregunta...', send: 'Enviar', typing: 'Escribiendo...' },
  de: { title: 'RIVE-Assistent', welcome: 'Hallo! Wie kann ich Ihnen helfen?', placeholder: 'Stellen Sie Ihre Frage...', send: 'Senden', typing: 'Schreibt...' },
  it: { title: 'Assistente RIVE', welcome: 'Ciao! Come posso aiutarla?', placeholder: 'Faccia la sua domanda...', send: 'Invia', typing: 'Sta scrivendo...' },
  pt: { title: 'Assistente RIVE', welcome: 'Olá! Como posso ajudá-lo?', placeholder: 'Faça a sua pergunta...', send: 'Enviar', typing: 'Digitando...' },
  ar: { title: 'مساعد RIVE', welcome: 'مرحبا! كيف يمكنني مساعدتك؟', placeholder: 'اطرح سؤالك...', send: 'إرسال', typing: 'جاري الكتابة...' },
  zh: { title: 'RIVE 助手', welcome: '你好！我能帮你什么？', placeholder: '请提问...', send: '发送', typing: '正在输入...' },
  uk: { title: 'Асистент RIVE', welcome: 'Привіт! Чим можу допомогти?', placeholder: 'Поставте запитання...', send: 'Надіслати', typing: 'Друкує...' },
  ru: { title: 'Ассистент RIVE', welcome: 'Здравствуйте! Чем могу помочь?', placeholder: 'Задайте вопрос...', send: 'Отправить', typing: 'Печатает...' },
  tr: { title: 'RIVE Asistanı', welcome: 'Merhaba! Size nasıl yardımcı olabilirim?', placeholder: 'Sorunuzu sorun...', send: 'Gönder', typing: 'Yazıyor...' },
  sw: { title: 'Msaidizi wa RIVE', welcome: 'Habari! Ninawezaje kukusaidia?', placeholder: 'Uliza swali...', send: 'Tuma', typing: 'Inaandika...' },
  wo: { title: 'Ndimbal RIVE', welcome: 'Nanga def! Naka laa la mën a dimbali?', placeholder: 'Laaj sa laaj...', send: 'Yónnee', typing: 'Dafay bind...' },
  am: { title: 'የRIVE ረዳት', welcome: 'ሰላም! እንዴት ልረዳዎ?', placeholder: 'ጥያቄዎን ይጠይቁ...', send: 'ላክ', typing: 'በመጻፍ ላይ...' },
};

function Chatbot() {
  const { i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const prevLangRef = useRef(i18n.language);

  const lang = i18n.language?.split('-')[0] || 'fr';
  const labels = CHATBOT_LABELS[lang] || CHATBOT_LABELS.fr;

  // Add welcome message when panel opens or language changes
  useEffect(() => {
    if (isOpen) {
      const currentLang = i18n.language?.split('-')[0] || 'fr';
      const currentLabels = CHATBOT_LABELS[currentLang] || CHATBOT_LABELS.fr;

      if (messages.length === 0 || prevLangRef.current !== currentLang) {
        setMessages([{ role: 'bot', content: currentLabels.welcome }]);
        prevLangRef.current = currentLang;
      }
    }
  }, [isOpen, i18n.language, messages.length]);

  // Scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isTyping]);

  // Focus input when panel opens
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  const sendMessage = async () => {
    const text = input.trim();
    if (!text || isTyping) return;

    const userMsg = { role: 'user', content: text };
    const newMessages = [...messages, userMsg];
    setMessages(newMessages);
    setInput('');
    setIsTyping(true);

    // Build history for API (exclude welcome message)
    const history = newMessages
      .filter(m => m.role !== 'bot' || newMessages.indexOf(m) !== 0)
      .map(m => ({ role: m.role === 'bot' ? 'model' : 'user', content: m.content }));

    try {
      const res = await fetch(`${API_BASE}/api/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: text, language: lang, history }),
      });

      if (!res.ok) throw new Error('API error');

      const data = await res.json();
      setMessages(prev => [...prev, { role: 'bot', content: data.reply }]);
    } catch {
      setMessages(prev => [...prev, { role: 'bot', content: lang === 'fr'
        ? 'Désolé, une erreur est survenue. Contactez-nous au 01 30 21 34 50.'
        : 'Sorry, an error occurred. Please contact us at 01 30 21 34 50.'
      }]);
    } finally {
      setIsTyping(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <>
      {isOpen && (
        <div className="chatbot-panel">
          <div className="chatbot-header">
            <span className="chatbot-header-title">{labels.title}</span>
            <button className="chatbot-close" onClick={() => setIsOpen(false)} aria-label="Close">
              <FaTimes />
            </button>
          </div>

          <div className="chatbot-messages">
            {messages.map((msg, i) => (
              <div key={i} className={`chatbot-msg chatbot-msg-${msg.role === 'user' ? 'user' : 'bot'}`}>
                {msg.content}
              </div>
            ))}
            {isTyping && <div className="chatbot-typing">{labels.typing}</div>}
            <div ref={messagesEndRef} />
          </div>

          <div className="chatbot-input-area">
            <input
              ref={inputRef}
              className="chatbot-input"
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={labels.placeholder}
            />
            <button
              className="chatbot-send"
              onClick={sendMessage}
              disabled={!input.trim() || isTyping}
              aria-label={labels.send}
            >
              <FaPaperPlane />
            </button>
          </div>
        </div>
      )}

      <button
        className="chatbot-bubble"
        onClick={() => setIsOpen(!isOpen)}
        aria-label={labels.title}
      >
        {isOpen ? <FaTimes /> : <FaCommentDots />}
      </button>
    </>
  );
}

export default Chatbot;
